import { Component, Prop, Vue } from 'vue-property-decorator';

@Component<MiButton>({})
export default class MiButton extends Vue {
  @Prop()
  protected options!: MiButtonOptions;

  /**
   * Handles the button click event
   *
   * @protected
   * @param {MouseEvent} event
   * @returns
   * @memberof MiButton
   */
  protected handleClick(event: MouseEvent) {
    if (! this.options) { return; }
    if (this.options && ! this.options.disabled) { this.$emit('click', event); }
  }

  /**
   * Sets the button margin classes based on the 'condensed' and / or 'fab' options
   *
   * @readonly
   * @protected
   * @type {string}
   * @memberof MiButton
   */
  protected get iconMargin(): string {
    if (! this.options || this.options.fab) { return ''; }
    let className = this.options.iconRight ? 'ml-' : 'mr-';
    className += this.options.condensed ? '8' : '16';
    return className;
  }

  /**
   * Sets the button padding classes based on the 'condensed' and / or 'fab' options
   *
   * @readonly
   * @protected
   * @type {string}
   * @memberof MiButton
   */
  protected get iconPadding(): string {
    if (this.options && this.options.condensed) {
      if (this.options.fab) {
        return 'p-4';
      }
      return 'py-4 px-8';
    }
    if (this.options && this.options.fab) {
      return 'p-8';
    }
    return 'py-8 px-24';
  }
}

export interface MiButtonOptions {
  /**
   * Sets the color of the button.
   * Defaults to ''primary''
   * Colors need to be declared in the tailwind.config under theme -> color
   * ex. 'primary', 'success'
   */
  color?: string;
  /**
   * Sets the color intensity of the button.
   * Defaults to 500
   * Needs to be declared in the tailwind.config under theme -> color
   * ex. '500', '200'
   */
  colorIntensity?: number;
  /**
   * (Optional)
   * If you want the button to route within the Vue Application use this property
   * creates a <router-link>
   */
  to?: string;
  /**
   * (Optional) sets an icon in the button
   * (use mdi icons https://materialdesignicons.com/ and prefix with 'mdi-')
   * Default icon postion is left of the button label
   */
  icon?: string;
  round?: boolean; // (Optional) Rounds the button edges
  outline?: boolean; // (Optional) Changes the look of the button from a filled to an outlined button
  disabled?: boolean; // (Optiona) Sets the buttons state to disabled
  loading?: boolean; // (Optional) Sets the buttons state to loading
  fab?: boolean; // (Optiona) Creates a Floating Action Button by removing the button label, centering the icon and settings the border-radius to full
  pill?: boolean; // (Optiona) Changes the look of the button to a Pill
  flat?: boolean; // (Optional) Remove the drops shadow of the button
  condensed?: boolean; // (Optional) Reduces the padding, creating a smaller sized button
  iconRight?: boolean; // (Optional) Changes the postion of the button icon to the right
  label?: string; // Sets the button label
  /**
   * (Optional)
   * When toolTipText is filled a hover action will show a tooltip for the button
   * The content of this tooltip will be equal to toolTipText
   */
  tooltipText?: string;
}
